import { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { formatVND, cn } from '@/lib/utils';
import { recurringCandidates, type TemplateCandidate } from '../../../finance';
import { importTemplates, type TemplateInput } from '../../../ledger/plans';
import { useData } from '../../store/data';
import { FormSheet } from '../../components/FormSheet';

const KIND_LABEL = { expense: 'Chi', income: 'Thu', payment: 'Trả thẻ/nợ' } as const;
/** Khoản xuất hiện từ ngần này tháng trở lên mới được tick sẵn; ít hơn có thể chỉ là khoản một lần. */
export const CONFIDENT = 3;

/** Chỉ số tháng (năm*12+tháng) → 'yyyy-mm'. */
const ymKey = (ym: number) => `${Math.floor((ym - 1) / 12)}-${String(((ym - 1) % 12) + 1).padStart(2, '0')}`;

/** Danh sách ứng viên mẫu định kỳ từ dữ liệu dự kiến cũ + tháng bắt đầu sinh khoản (tháng liền sau dữ liệu cũ gần nhất). */
export const useImportCandidates = () => {
  const { plannedExpenses, plannedIncomes, plannedPayments, recurringTemplates } = useData(s => s.data);
  return useMemo(() => {
    const cands: TemplateCandidate[] = [
      ...recurringCandidates('expense', plannedExpenses, recurringTemplates),
      ...recurringCandidates('income', plannedIncomes, recurringTemplates),
      ...recurringCandidates('payment', plannedPayments, recurringTemplates)
    ];
    const all = [...plannedExpenses, ...plannedIncomes, ...plannedPayments].filter((r: any) => r.isRecurring && r.year && r.month);
    const latest = all.length ? Math.max(...all.map((r: any) => r.year * 12 + r.month)) : 0;
    // Mẫu bắt đầu sinh từ tháng liền SAU dữ liệu định kỳ cũ gần nhất → không trùng khoản đã có
    return { cands, startMonth: ymKey((latest || (new Date().getFullYear() * 12 + new Date().getMonth())) + 1) };
  }, [plannedExpenses, plannedIncomes, plannedPayments, recurringTemplates]);
};

export default function ImportTemplatesSheet({ onClose }: { onClose: () => void }) {
  const { cands, startMonth } = useImportCandidates();
  const sources = useData(s => s.data.sources);
  const walletOk = (c: TemplateCandidate) => sources.some(w => w.id === c.walletId && w.status !== 'inactive');
  const keyOf = (c: TemplateCandidate, i: number) => `${c.kind}:${i}`;
  const [picked, setPicked] = useState<Record<string, boolean>>(() => Object.fromEntries(cands.map((c, i) => [keyOf(c, i), c.seenMonths >= CONFIDENT && walletOk(c)])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = cands.filter((c, i) => picked[keyOf(c, i)]);
  const submit = async () => {
    if (chosen.length === 0) { setError('Chưa chọn khoản nào.'); return; }
    setBusy(true); setError(null);
    const inputs: TemplateInput[] = chosen.map(c => ({
      type: c.kind, name: c.name, amount: c.amount, category: c.category ?? '', walletId: c.walletId, walletName: c.walletName,
      frequency: 'monthly', dayOfMonth: c.day, active: true, startMonth,
      ...(c.kind === 'payment' ? { paymentType: c.paymentType, creditCardId: c.creditCardId, creditCardName: c.creditCardName, debtId: c.debtId, debtName: c.debtName } : {})
    }));
    const r = await importTemplates(getAuth().currentUser!.uid, inputs);
    setBusy(false);
    if (!r.success) { setError(r.error ?? 'Không nhập được'); return; }
    toast.success(`Đã tạo ${r.count} mẫu định kỳ — bắt đầu sinh từ tháng ${startMonth.slice(5)}/${startMonth.slice(0, 4)}`); onClose();
  };

  return (
    <FormSheet open onClose={onClose} title="Nhập định kỳ từ dữ liệu cũ" onSubmit={submit} submitLabel={`Tạo ${chosen.length} mẫu`} busy={busy} error={error}
      description={`Gom từ các khoản định kỳ của tháng gần nhất. Mẫu bắt đầu sinh khoản từ tháng ${startMonth.slice(5)}/${startMonth.slice(0, 4)}, nên không trùng với khoản đã có. Số tiền chỉ là gợi ý — đổi được khi xác nhận hoặc sửa mẫu sau.`}>
      {cands.length === 0 ? <p className="text-sm text-muted-foreground">Không còn khoản định kỳ nào chưa có mẫu.</p> : (
        <ul className="space-y-2">
          {cands.map((c, i) => {
            const k = keyOf(c, i); const ok = walletOk(c);
            return (
              <li key={k}>
                <label className={cn('flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3', !ok && 'cursor-not-allowed opacity-50', picked[k] && 'border-primary/50 bg-accent/40')}>
                  <input type="checkbox" className="mt-1 h-4 w-4 accent-primary" checked={!!picked[k]} disabled={!ok}
                    onChange={e => setPicked(p => ({ ...p, [k]: e.target.checked }))} aria-label={`Nhận mẫu ${c.name}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-medium">{c.name}</span>
                      <span className="num shrink-0 text-sm font-semibold">{formatVND(c.amount)}</span>
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {KIND_LABEL[c.kind]} · ngày {c.day} hằng tháng · {c.walletName || 'ví'}{c.category ? ` · ${c.category}` : ''}
                    </span>
                    <span className={cn('block text-xs', c.seenMonths >= CONFIDENT ? 'text-muted-foreground' : 'text-warn')}>
                      {!ok ? 'Ví này không còn dùng — không tạo mẫu được.' : c.seenMonths >= CONFIDENT ? `Đã lặp ${c.seenMonths} tháng` : `Mới thấy ${c.seenMonths} tháng — có thể chỉ là khoản một lần`}
                      {c.kind === 'payment' && ok ? ' · số tiền trả thẻ/nợ hay thay đổi, sửa lại khi xác nhận' : ''}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </FormSheet>
  );
}
